/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'

// Components
import {
    Flex,
    AspectRatio,
    Box,
    Skeleton as ChakraSkeleton,
    Text,
    useMultiStyleConfig
} from '@chakra-ui/react'
import DynamicImage from '../dynamic-image'
import Swatch from '../../components/swatch-group/swatch'

// Hooks
import {useTheme} from '@chakra-ui/react'
import {useProduct} from '../../hooks'
import useMultiSite from '../../hooks/use-multi-site'

// Other
import {useColorVariation} from '../../hooks'
import {findImageGroupBy} from '../../utils/image-groups-utils'
import {PRODUCT_TILE_IMAGE_TYPE} from '../../constants'
import ProductAvailability from '../../pages/product-detail/partials/product-availability'
import {useMaterialVariation} from '../../hooks/use-variation-attributes'
import Chip from '../chip'
import {noop} from '../../utils/utils'
import {useHistory} from 'react-router-dom'

// Component Skeleton
export const Skeleton = (props) => {
    const styles = useMultiStyleConfig('ProductTile')
    return (
        <Box data-testid="sf-product-tile-skeleton" {...props}>
            <Flex direction="column" gap={2}>
                <Box {...styles.imageWrapper}>
                    <AspectRatio ratio={1} {...styles.image}>
                        <ChakraSkeleton />
                    </AspectRatio>
                </Box>
                <ChakraSkeleton width="80px" height="20px" />
                <ChakraSkeleton width={{base: '120px', md: '220px'}} height="12px" />
            </Flex>
        </Box>
    )
}

/**
 * The ProductSetTile is a visual representation of a product set object:
 *  - The [Default] variation will show its images, name, selected material and color
 */
const ProductSetTile = ({product, dynamicImageProps, onOpen, isProductASet, productSetIndex}) => {
    const {site} = useMultiSite()
    const styles = useMultiStyleConfig('ProductSetTile')
    const {variationParams} = useProduct(product, isProductASet, productSetIndex)
    const {imageGroups, name, productName} = product
    const theme = useTheme()

    /* We use localized id and name because getProducts & getProduct returning different responses */
    const localizedProductName = name || productName

    /**
     * Filter [imageGroups] by PRODUCT_TILE_IMAGE_TYPE and selected color variation.
     * Slice them because we need only first and second image
     */
    const images = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: PRODUCT_TILE_IMAGE_TYPE,
                selectedVariationAttributes: variationParams
            })?.images?.slice(0, 2),
        [variationParams, imageGroups]
    )

    /* Initialize [mainImage] with the first image of current color variation */
    const [mainImage, setMainImage] = useState(images && images[0])

    useEffect(() => {
        setMainImage(images && images[0])
    }, [variationParams, images])

    /* Get extended variation attributes data with images etc. */
    // const colorVariations = useColorVariation(product)?.values
    const colorVariation = useColorVariation(product, isProductASet, productSetIndex)
    const materialVariation = useMaterialVariation(product)

    const selectedColor = useMemo(
        () =>
            colorVariation?.values?.find(
                ({value}) => value === colorVariation.selectedValue.value
            ) || colorVariation?.values?.[0],
        [colorVariation]
    )
    const selectedMaterial = useMemo(
        () =>
            materialVariation?.values?.find(
                ({value}) => value === materialVariation.selectedValue.value
            ) || materialVariation?.values?.[0],
        [materialVariation]
    )

    const colors = useMemo(
        () =>
            [
                selectedColor,
                ...(colorVariation?.values?.filter(
                    ({value, orderable}) =>
                        orderable && value !== colorVariation.selectedValue.value
                ) || [])
            ].filter(Boolean),
        [selectedColor, colorVariation]
    )
    const displayColors = useMemo(() => colors.slice(0, colors.length > 4 ? 3 : 4), [colors])

    const history = useHistory()

    return (
        <Box {...styles.container} data-testid="product-set-tile" onClick={onOpen}>
            <Flex gap={2}>
                <Box>
                    {/* Image */}
                    {product && images && (
                        <Box
                            position="relative"
                            onMouseEnter={(e) => {
                                e.preventDefault()
                                images[1] && setMainImage(images[1])
                            }}
                            onMouseLeave={(e) => {
                                e.preventDefault()
                                images[1] && setMainImage(images[0])
                            }}
                            {...styles.imageWrapper}
                        >
                            <Box
                                position="absolute"
                                width="100%"
                                height="100%"
                                backgroundImage={theme.gradients.greyBackgroundOfProductImages}
                            ></Box>
                            <DynamicImage
                                src={`${mainImage.disBaseLink || mainImage.link}[?sw={width}&q=60]`}
                                widths={dynamicImageProps?.widths}
                                imageProps={{
                                    alt: mainImage.alt,
                                    objectFit: 'contain',
                                    ...dynamicImageProps?.imageProps
                                }}
                            />
                        </Box>
                    )}
                </Box>
                <Flex direction="column" gap={4} minWidth={0}>
                    {/* Title */}
                    <Text textStyle="Regular Medium" minWidth={0}>
                        {localizedProductName}
                    </Text>

                    <Flex direction="column" gap={2}>
                        {/*Material*/}
                        {selectedMaterial && (
                            <Chip
                                href={selectedMaterial.href}
                                value={selectedMaterial.value}
                                name={selectedMaterial.name}
                                image={selectedMaterial.image}
                                orderable={selectedMaterial.orderable}
                                selected={true}
                            />
                        )}

                        {/*Selected color*/}
                        {selectedColor && (
                            <Flex gap={2} alignItems="center">
                                <Text textStyle="Regular Small" minWidth={0}>
                                    {selectedColor.name}
                                </Text>
                                {displayColors.map((v) => (
                                    <Swatch
                                        key={v.value}
                                        value={v.value}
                                        name={v.name}
                                        href={v.href}
                                        disabled={!v.orderable}
                                        selected={v.value === selectedColor.value}
                                        size="sm"
                                        onChange={(value, href) => {
                                            if (!href) return
                                            history.replace(href)
                                        }}
                                    >
                                        {selectedColor.image ? (
                                            <Box
                                                height="100%"
                                                width="100%"
                                                minWidth="24px"
                                                minHeight="24px"
                                                backgroundRepeat="no-repeat"
                                                backgroundSize="cover"
                                                backgroundColor={v.name.toLowerCase()}
                                                backgroundImage={
                                                    v.image
                                                        ? `url("${encodeURI(
                                                              v.image.disBaseLink || v.image.link
                                                          )}")`
                                                        : ''
                                                }
                                            />
                                        ) : (
                                            v.name
                                        )}
                                    </Swatch>
                                ))}
                                {displayColors.length < colors.length && (
                                    <Swatch
                                        selected
                                        size="sm"
                                        onChange={noop}
                                        onClicK={() => () => {
                                            onOpen()
                                        }}
                                    >
                                        +{Math.min(99, colors.length - displayColors.length)}
                                    </Swatch>
                                )}
                            </Flex>
                        )}
                    </Flex>
                </Flex>
            </Flex>
            {!site?.isContentOnlySite && <ProductAvailability flexShrink="0" product={product} />}
        </Box>
    )
}

ProductSetTile.displayName = 'ProductSetTile'

ProductSetTile.defaultProps = {
    variation: 'Default'
}

ProductSetTile.propTypes = {
    /**
     * The product search hit that will be represented in this
     * component.
     */
    product: PropTypes.shape({
        currency: PropTypes.string,
        image: PropTypes.shape({
            alt: PropTypes.string,
            disBaseLink: PropTypes.string,
            link: PropTypes.string
        }),
        price: PropTypes.number,
        // `name` is present and localized when `product` is provided by a RecommendedProducts component
        // (from Shopper Products `getProducts` endpoint), but is not present when `product` is
        // provided by a ProductList component.
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-products?meta=getProducts
        name: PropTypes.string,
        // `productName` is localized when provided by a ProductList component (from Shopper Search
        // `productSearch` endpoint), but is NOT localized when provided by a RecommendedProducts
        // component (from Einstein Recommendations `getRecommendations` endpoint).
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-search?meta=productSearch
        // See: https://developer.salesforce.com/docs/commerce/einstein-api/references/einstein-api-quick-start-guide?meta=getRecommendations
        // Note: useEinstein() transforms snake_case property names from the API response to camelCase
        productName: PropTypes.string,
        id: PropTypes.string,
        productId: PropTypes.string,

        // Example of variationAttributes array:
        // [
        //     {
        //         id: 'color',
        //         name: 'Farbe',
        //         values: [
        //             {name: 'Deep Grey White Chassis', orderable: false, value: '144'},
        //             {name: 'Grey Clouds', orderable: true, value: '386'}
        //         ]
        //     }
        // ]
        variationAttributes: PropTypes.array,

        // Example of variationValues object:
        // {
        //     color: '110'
        // }
        variationValues: PropTypes.object,
        imageGroups: PropTypes.array
    }),
    dynamicImageProps: PropTypes.object,
    variation: PropTypes.string,
    onOpen: PropTypes.func,
    productSetIndex: PropTypes.number,
    isProductASet: PropTypes.bool
}

export default ProductSetTile
